/**
 * AI 一括登録ウィザード
 *
 * job.status を唯一の状態源にして画面を出し分ける。生成と場所解決はサーバ側で走るので、
 * 進行中は一定間隔で取り直す。画面を離れても続くため、開き直したときは進行中のジョブに合流する。
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'

import {
  applyImportJob,
  confirmImportSpots,
  createImportJob,
  deleteImportJob,
  getImportJob,
  listImportJobs,
  patchImportCandidates,
} from '@/api/services/stamp-rally'
import type {
  CandidatePatchParams,
  ConfirmSpotParams,
  ImportJob,
  ImportJobStatus,
  ImportSourceType,
} from '@/api/types/stamp-rally'
import { Header } from '@/components/shared/header'
import { Button } from '@/components/ui/button'
import { useGoBack } from '@/hooks/useGoBack'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { PATHS } from '@/routes/paths'
import { useStampRallyStore } from '@/store/useStampRallyStore'

import { ImportCandidateList } from './components/import-candidate-list'
import { ImportProgress } from './components/import-progress'
import { ImportSourceForm } from './components/import-source-form'
import { ImportSpotReview } from './components/import-spot-review'

const POLLING_STATUSES: ImportJobStatus[] = ['pending', 'generating', 'resolving']
const POLLING_INTERVAL_MS = 2000

const messageOf = (error: unknown, fallback: string): string => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const detail = (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail
    if (typeof detail === 'string') return detail
  }
  return fallback
}

export const RallyImportPage = () => {
  const navigate = useNavigate()
  const goBack = useGoBack()
  const { rally_id: rallyIdParam } = useParams<{ rally_id: string }>()
  const rallyId = Number(rallyIdParam)

  const [job, setJob] = useState<ImportJob | null>(null)
  const { isSubmitting, run } = useSubmitLock()
  const fetchDetail = useStampRallyStore((s) => s.fetchDetail)

  // 取り直しで job が差し替わってもこの 2 つが変わらなければタイマーを張り直さない
  const jobId = job?.id ?? null
  const shouldPoll = job !== null && POLLING_STATUSES.includes(job.status)

  useEffect(() => {
    if (!Number.isFinite(rallyId)) return
    const controller = new AbortController()
    listImportJobs(rallyId, controller.signal)
      .then(async (jobs) => {
        const active = jobs.at(0)
        if (!active) return
        // 一覧は候補を持たないので、合流するときは詳細を取り直す
        setJob(await getImportJob(active.id, controller.signal))
      })
      .catch(() => undefined)
    return () => controller.abort()
  }, [rallyId])

  useEffect(() => {
    if (jobId === null || !shouldPoll) return
    const timer = window.setInterval(() => {
      getImportJob(jobId)
        .then(setJob)
        .catch(() => undefined)
    }, POLLING_INTERVAL_MS)
    return () => window.clearInterval(timer)
  }, [jobId, shouldPoll])

  const handleCreate = useCallback(
    async (sourceType: ImportSourceType, sourceInput: string, searchPrefix: string, searchSuffix: string) => {
      await run(async () => {
        try {
          setJob(
            await createImportJob({
              rally_id: rallyId,
              source_type: sourceType,
              source_input: sourceInput,
              search_prefix: searchPrefix || null,
              search_suffix: searchSuffix || null,
            }),
          )
        } catch (error) {
          toast.error(messageOf(error, '取り込みを開始できませんでした'))
        }
      })
    },
    [rallyId, run],
  )

  const handleConfirm = async (spots: ConfirmSpotParams[], searchPrefix: string, searchSuffix: string) => {
    if (!job) return
    await run(async () => {
      try {
        setJob(await confirmImportSpots(job.id, spots, searchPrefix || null, searchSuffix || null))
      } catch (error) {
        toast.error(messageOf(error, '確定できませんでした'))
      }
    })
  }

  const handleApply = async (patches: CandidatePatchParams[]) => {
    if (!job) return
    await run(async () => {
      try {
        if (patches.length > 0) await patchImportCandidates(job.id, patches)
        const result = await applyImportJob(job.id)
        await fetchDetail(rallyId)
        toast.success(`${result.created} 件を登録しました`)
        navigate(PATHS.RALLIES.DETAIL.replace(':rally_id', String(rallyId)))
      } catch (error) {
        toast.error(messageOf(error, '登録できませんでした'))
      }
    })
  }

  const handleRestart = async () => {
    if (!job) return
    try {
      await deleteImportJob(job.id)
    } catch {
      // 消せなくても入力画面には戻す
    }
    setJob(null)
  }

  const renderBody = () => {
    if (!job) {
      return (
        <ImportSourceForm
          isSubmitting={isSubmitting}
          onSubmit={(type, value, prefix, suffix) => void handleCreate(type, value, prefix, suffix)}
        />
      )
    }

    if (job.status === 'failed') {
      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
          <p className="text-sm">{job.error_message ?? '取り込みに失敗しました'}</p>
          <Button variant="outline" onClick={() => void handleRestart()}>
            やり直す
          </Button>
        </div>
      )
    }

    if (job.status === 'pending' || job.status === 'generating') {
      return (
        <ImportProgress
          title="スポットを探しています"
          description="AI が一覧を作っています。場所検索はまだ実行していません。"
        />
      )
    }

    if (job.status === 'spots_ready') {
      return (
        <ImportSpotReview
          // 確認① は内部で編集状態を持つので、別のジョブになったら作り直す
          key={job.id}
          candidates={job.candidates}
          isSubmitting={isSubmitting}
          initialPrefix={job.search_prefix ?? ''}
          initialSuffix={job.search_suffix ?? ''}
          onConfirm={(spots, prefix, suffix) => void handleConfirm(spots, prefix, suffix)}
        />
      )
    }

    if (job.status === 'resolving') {
      return (
        <ImportProgress
          title="場所を調べています"
          description="選んだスポットの住所と座標を取得しています。"
          done={job.progress_done}
          total={job.progress_total}
        />
      )
    }

    return (
      <ImportCandidateList
        candidates={job.candidates}
        isSubmitting={isSubmitting}
        onApply={(patches) => void handleApply(patches)}
      />
    )
  }

  return (
    <div className="flex h-dvh flex-col">
      <Header title="まとめて登録" onClose={goBack} />
      {renderBody()}
    </div>
  )
}
